/**
 * La recherche dans la liste des fuseaux : la saisie de la personne → les lignes à montrer.
 *
 * Pure, sur des `UITimezoneOption` : le design system l'appelle depuis `<TimezonePicker>`, qui
 * garde la saisie (un champ en cours de saisie est de l'état d'interface).
 *
 * Accents et casse ne comptent pas (« dubai » trouve « Dubaï »), et une ville qui COMMENCE par la
 * saisie passe avant celles qui la contiennent seulement. La liste montrée est bornée : quatre
 * cents lignes ne se parcourent pas, elles se cherchent.
 */
import { type UITimezoneOption } from "../types"

/**
 * La phrase sous « Fuseau automatique » : ce que fait le réglage dans l'état où il est. En mode
 * parent, le compte est celui de l'enfant et l'app n'envoie pas le fuseau du téléphone du parent :
 * l'automatique suit l'appareil DE L'ENFANT (et l'espace parent vouvoie).
 */
export function timezoneAutoHint(parentMode: boolean, auto: boolean = true): string {
    if (auto) {
        return parentMode
            ? "Le fuseau suit celui de l'appareil de votre enfant. Coupez-le pour en choisir un à la main."
            : "L'app suit le fuseau de cet appareil. Coupe-le pour choisir ton fuseau à la main."
    }

    return parentMode
        ? "Fuseau choisi à la main : il ne bouge plus. Rallumez-le pour suivre l'appareil de votre enfant."
        : "Fuseau choisi à la main : il ne bouge plus, même en voyage. Rallume-le pour suivre cet appareil."
}

/** Le nombre de lignes montrées à la fois. */
export const TIMEZONE_RESULTS_MAX = 30

/** « Dubaï » → « dubai » : sans accent ni capitale, pour comparer ce qu'on tape à ce qu'on lit. */
export function foldForSearch(text: string): string {
    const lower = text.toLowerCase().trim()
    return typeof lower.normalize === "function" ? lower.normalize("NFD").replace(/[̀-ͯ]/g, "") : lower
}

export type TimezoneSearchResult = {
    shown: UITimezoneOption[]
    /** Ce qui correspond encore au-delà de la borne — l'écran invite à préciser. */
    hidden: number
}

export function searchTimezoneOptions(
    options: UITimezoneOption[],
    query: string,
    max: number = TIMEZONE_RESULTS_MAX
): TimezoneSearchResult {
    const needle = foldForSearch(query).replace(/[\s_]+/g, " ")

    const matches =
        needle === ""
            ? options
            : options
                  .map((option) => ({ option, rank: matchRank(option, needle) }))
                  .filter((entry) => entry.rank >= 0)
                  // `sort` est stable : à rang égal, l'ordre du serveur reste.
                  .sort((a, b) => a.rank - b.rank)
                  .map((entry) => entry.option)

    return { shown: matches.slice(0, max), hidden: Math.max(0, matches.length - max) }
}

/** 0 : la ville commence par la saisie ; 1 : elle la contient ; 2 : la région ou l'identifiant ; -1 : rien. */
function matchRank(option: UITimezoneOption, needle: string): number {
    const city = foldForSearch(option.city)
    if (city.startsWith(needle)) return 0
    if (city.includes(needle)) return 1
    const elsewhere = [option.label, option.id.replace(/_/g, " ")].map(foldForSearch)
    return elsewhere.some((text) => text.includes(needle)) ? 2 : -1
}
